/**
 * CMS Content Hooks (Decap CMS Secure Static Bridge)
 *
 * Provides React hooks for fetching marketing page content, settings,
 * and blog posts from local Decap CMS markdown/YAML files.
 * This completely replaces the Sanity.io API connection, securing the app
 * against external authentication issues and variant dependency hijack attacks (CWE-427).
 */

import { useState, useEffect } from 'react';
import { getInitialPageContent, loadPageContent, type PageContent } from './contentLoader';
import {
  loadGeneralSettings,
  loadNavigationSettings,
  loadSEOSettings,
  loadScriptsSettings,
  type MenuItem,
} from './siteSettings';
import { blogPosts, type BlogPost } from '../data/blogPosts';

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface UseSanityResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface PageData {
  title?: string;
  description?: string;
  body?: string;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: { asset: { url: string } };
    twitterImage?: { asset: { url: string } };
  };
}

export interface PageSummary {
  slug: { current: string };
  title: string;
}

export interface SEOSettingsData {
  defaultTitle?: string;
  defaultDescription?: string;
  defaultKeywords?: string;
  defaultOgImage?: { asset: { url: string } };
  twitterHandle?: string;
  facebookAppId?: string;
}

export interface SiteSettingsData {
  companyName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface NavigationItem {
  label: string;
  url: string;
}

export interface NavigationMenu {
  label: string;
  url: string;
  children?: NavigationItem[];
}

export interface NavigationData {
  mainMenu: NavigationMenu[];
}

export interface ScriptsSettingsData {
  headScripts?: string;
  footerScripts?: string;
  robotsTxt?: string;
}

// ─── CMS Hooks ──────────────────────────────────────────────────────────────

function toPageData(content: PageContent | null): PageData | null {
  if (!content) return null;
  return {
    title: content.title,
    description: content.description,
    body: content.body,
    seo: {
      metaTitle: content.seo_title,
      metaDescription: content.seo_description,
      ogImage: content.og_image ? { asset: { url: content.og_image } } : undefined,
      twitterImage: content.twitter_image ? { asset: { url: content.twitter_image } } : undefined,
    },
  };
}

/**
 * Fetch a single page by slug from Decap CMS markdown content.
 */
export function useSanityPage(slug: string): UseSanityResult<PageData> {
  const [data, setData] = useState<PageData | null>(() => toPageData(getInitialPageContent(slug)));
  const [loading, setLoading] = useState(() => getInitialPageContent(slug) === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initial = getInitialPageContent(slug);
    if (initial) {
      setData(toPageData(initial));
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const content = await loadPageContent(slug);
        if (active) {
          setData(toPageData(content));
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
          console.warn(`[CMS] Failed to load local page content for ${slug}:`, err);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [slug]);

  return { data, loading, error };
}

/**
 * Fetch all pages from Decap CMS (for sitemap, nav).
 */
export function useSanityPages(): UseSanityResult<PageSummary[]> {
  const [data, setData] = useState<PageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Return local list of known pages for sitemaps
    setData([
      { slug: { current: 'home' }, title: 'Home' },
      { slug: { current: 'features' }, title: 'Features' },
      { slug: { current: 'why-rooflyzer' }, title: 'Why Rooflyzer' },
      { slug: { current: 'pricing' }, title: 'Pricing' },
      { slug: { current: 'for-roofers' }, title: 'For Roofers' },
      { slug: { current: 'contractors' }, title: 'For Contractors' },
      { slug: { current: 'agencies' }, title: 'For Agencies' },
      { slug: { current: 'integrations' }, title: 'Integrations' },
      { slug: { current: 'about' }, title: 'About' },
      { slug: { current: 'faq' }, title: 'FAQ' },
      { slug: { current: 'security' }, title: 'Security' },
      { slug: { current: 'contact' }, title: 'Contact' },
    ]);
    setLoading(false);
    setError(null);
  }, []);

  return { data, loading, error };
}

/**
 * Fetch a single blog post by slug from the secure local database synchronously.
 */
export function useSanityBlogPost(slug: string): UseSanityResult<BlogPost> {
  const post = blogPosts.find((p) => p.slug.current === slug) ?? null;
  return {
    data: post,
    loading: false,
    error: post ? null : new Error(`Blog post with slug "${slug}" not found`),
  };
}

/**
 * Read bundled blog posts synchronously, including during server rendering.
 */
export function useSanityBlogPosts(): UseSanityResult<BlogPost[]> {
  return { data: blogPosts, loading: false, error: null };
}

/**
 * Fetch global SEO settings from CMS-managed seo.yml.
 */
export function useSanitySEOSettings(): UseSanityResult<SEOSettingsData> {
  const [data, setData] = useState<SEOSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const seo = await loadSEOSettings();
        if (active && seo) {
          setData({
            defaultTitle: seo.default_title,
            defaultDescription: seo.default_description,
            defaultKeywords: '',
            defaultOgImage: seo.default_og_image
              ? { asset: { url: seo.default_og_image } }
              : undefined,
            twitterHandle: seo.twitter_handle,
            facebookAppId: seo.facebook_app_id,
          });
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}

/**
 * Fetch global site settings from CMS-managed general.yml.
 */
export function useSanitySiteSettings(): UseSanityResult<SiteSettingsData> {
  const [data, setData] = useState<SiteSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const general = await loadGeneralSettings();
        if (active && general) {
          setData({
            companyName: general.company_name,
            address: general.address,
            city: general.city,
            state: general.state,
            zip: general.zip,
            phone: general.phone,
            email: general.email,
            website: general.website,
          });
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}

/**
 * Fetch navigation menu structure from CMS-managed navigation.yml.
 */
export function useSanityNavigation(): UseSanityResult<NavigationData> {
  const [data, setData] = useState<NavigationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const nav = await loadNavigationSettings();
        if (active && nav) {
          setData({
            mainMenu:
              nav.menu_items?.map((item: MenuItem) => ({
                label: item.label,
                url: item.url,
                children: item.children?.map((child: MenuItem) => ({
                  label: child.label,
                  url: child.url,
                })),
              })) || [],
          });
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}

/**
 * Fetch scripts/robots settings from CMS-managed scripts.yml.
 */
export function useSanityScriptsSettings(): UseSanityResult<ScriptsSettingsData> {
  const [data, setData] = useState<ScriptsSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const scripts = await loadScriptsSettings();
        if (active && scripts) {
          setData({
            headScripts: scripts.head_scripts,
            footerScripts: scripts.footer_scripts,
            robotsTxt: scripts.robots_txt,
          });
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}

/**
 * Cache invalidation dummy function (kept for backward compatibility).
 */
export function invalidateSanityCache(): void {
  // Static content doesn't require real-time cache purging
}
